import type { CityConfig } from "../hawaii-city/types";

export const lihueConfig: CityConfig = {
  city: "Lihue",
  state: "Hawaii",
  stateAbbr: "HI",
  slug: "lihue",
  route: "/cities/lihue",
  title: "Emergency & Expedited Passport Services in Lihue, HI — Kauai's Passport Hub | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Lihue, HI — Kauai's passport hub. Fast passport renewal and urgent passport help for Garden Isle travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Lihue, HI",
  heroSubtitle: "Lihue, Kapaa, and Kauai travelers — get your passport fast without leaving the Garden Isle. Start online, ship overnight, and we handle the rest. As fast as 24 hours.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Lush%20tropical%20landscape%20of%20Lihue%20Kauai%20Hawaii%20with%20Kalapaki%20Bay%2C%20coconut%20palm%20trees%20and%20golden%20sand%2C%20warm%20golden%20hour%20sunlight%2C%20distant%20mountains%20with%20green%20vegetation%2C%20professional%20travel%20photography%2C%20vibrant%20warm%20colors%20with%20deep%20blue%20ocean%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=lihue-hero-01&orientation=landscape",
  heroImageAlt: "Lihue Kauai tropical landscape with Kalapaki Bay and palm trees",
  geoPosition: "21.9811;-159.3711",
  geoPlacename: "Lihue, Hawaii",
  regionalAgency: {
    name: "Honolulu Passport Agency",
    address: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
    distance: "Inter-island flight to Honolulu — about 100 miles",
  },
  nearbyCitiesZips: [
    { name: "Lihue", zips: "96766" },
    { name: "Kapaa", zips: "96746" },
    { name: "Koloa", zips: "96756" },
    { name: "Kalaheo", zips: "96741" },
    { name: "Poipu", zips: "96756" },
    { name: "Hanalei", zips: "96714" },
    { name: "Princeville", zips: "96722" },
    { name: "Kilauea", zips: "96754" },
    { name: "Eleele", zips: "96705" },
    { name: "Hanapepe", zips: "96716" },
    { name: "Waimea", zips: "96796" },
    { name: "Kekaha", zips: "96752" },
    { name: "Honolulu", zips: "96815" },
    { name: "Kailua", zips: "96734" },
    { name: "Kahului", zips: "96732" },
    { name: "Hilo", zips: "96720" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your Lihue or Kapaa home in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping from Lihue to send your documents directly to us." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots — no public appointment needed for Kauai residents." },
  ],
  testimonials: [
    {
      name: "Emily W.",
      location: "Lihue, HI",
      rating: 5,
      text: "I had a business trip to Tokyo in 5 days and my passport was expired. Fast Passport Center processed my renewal from Lihue in 4 days. The shipping from Kauai was seamless and their team kept me updated.",
      service: "Passport Renewal",
    },
    {
      name: "John P.",
      location: "Kapaa, HI",
      rating: 5,
      text: "Our family trip to Europe was in 2 weeks and my daughter's passport was nowhere to be found. They walked us through the replacement process from Kapaa and had a new passport with time to spare.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Grace K.",
      location: "Poipu, HI",
      rating: 5,
      text: "Emergency trip to Australia for a family matter. I'm in Poipu near Lihue and FPC handled everything remotely. The passport arrived in 5 days. No need to fly to Honolulu for an agency appointment.",
      service: "Expedited Passport",
    },
  ],
  facilities: [
    {
      id: "lihue-usps",
      name: "Lihue Post Office",
      type: "USPS Passport Acceptance",
      address: "4441 Rice St",
      city: "Lihue",
      state: "HI",
      zip: "96766",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Lihue",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Appointment required. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "kapaa-usps",
      name: "Kapaa Post Office",
      type: "USPS Passport Acceptance",
      address: "4-1101 Kuhio Hwy",
      city: "Kapaa",
      state: "HI",
      zip: "96746",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "About 8 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "East Side Kauai option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "koloa-usps",
      name: "Koloa Post Office",
      type: "USPS Passport Acceptance",
      address: "5374 Koloa Rd",
      city: "Koloa",
      state: "HI",
      zip: "96756",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "South Shore — about 12 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "South Shore Kauai option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "hanalei-usps",
      name: "Hanalei Post Office",
      type: "USPS Passport Acceptance",
      address: "5-5155 Kuhio Hwy",
      city: "Hanalei",
      state: "HI",
      zip: "96714",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "North Shore — about 30 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "North Shore Kauai option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Lihue, HI?",
      a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours. Standard processing through a Lihue or Kapaa post office can take 2–6 months. For Kauai residents, our courier service is the fastest option without flying to Honolulu.",
    },
    {
      q: "Do I need to fly to Honolulu for a passport?",
      a: "No. Most Lihue and Kauai travelers start online and ship documents via overnight courier. The Lihue, Kapaa, Koloa, or Hanalei Post Offices can serve as acceptance facilities if needed, but you don't need to visit the Honolulu agency.",
    },
    {
      q: "Where is the closest passport acceptance facility to Lihue?",
      a: "The Lihue Post Office at 4441 Rice St accepts passport applications by appointment. Kapaa Post Office, Koloa Post Office, and Hanalei Post Office are also options for other parts of Kauai.",
    },
    {
      q: "What if I'm a tourist in Poipu or Princeville and need a passport emergency?",
      a: "We frequently help tourists and vacationers in Lihue, Poipu, Princeville, and Hanalei who discover passport issues while traveling. We can ship to your resort or hotel address and coordinate return shipping so you don't miss your flight.",
    },
    {
      q: "Can I get an expedited passport if I've never had one before?",
      a: "Yes, but first-time applicants require an in-person acceptance clerk visit. The Lihue or Kapaa Post Office are the most convenient options for Kauai residents. We guide you through exactly what to bring.",
    },
    {
      q: "How does shipping from Lihue work?",
      a: "You can ship overnight using FedEx or UPS from Lihue or Kapaa. Most Kauai residents use the FedEx location near Lihue Airport or the UPS Store in Kukui Grove.",
    },
    {
      q: "Is Fast Passport Center an official government agency?",
      a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We work within the official passport system to provide expedited processing.",
    },
    {
      q: "What if I need a child passport in Lihue?",
      a: "Child passports (for minors 15 and under) require parental participation and an in-person acceptance step — both parents typically must consent. We provide clear instructions for Kauai families.",
    },
    {
      q: "Can I get a passport if mine is lost or stolen?",
      a: "Yes. Lost or stolen passport replacements often require additional forms (including Form DS-64). We guide Kauai residents through the exact process, including the acceptance clerk visit and expedited submission.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Lihue", address: "3-3300 Kuhio Hwy, Lihue, HI 96766", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Kapaa", address: "4-1101 Kuhio Hwy, Kapaa, HI 96746", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — Lihue", address: "3-2600 Kaumualii Hwy, Lihue, HI 96766", hours: "Daily 7 AM – 10 PM" },
    { name: "The UPS Store — Lihue", address: "3-3200 Kuhio Hwy, Lihue, HI 96766", hours: "Call for hours" },
    { name: "Walmart — Lihue", address: "3-3300 Kuhio Hwy, Lihue, HI 96766", hours: "Call for hours" },
  ],
  dmvLocations: [
    {
      name: "Lihue DMV — Kauai County Service Center",
      address: "4444 Rice St, Lihue, HI 96766",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Kapaa DMV — Kapaa Satellite City Hall",
      address: "4-1577 Kuhio Hwy, Kapaa, HI 96746",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "East Side Kauai option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "LIH",
      name: "Lihue Airport",
      distance: "Primary airport — Kauai",
      address: "3901 Mokulele Loop, Lihue, HI 96766",
      note: "Major airport for Kauai with direct flights to mainland U.S. and inter-island connections. Most Kauai travelers fly from LIH for international trips.",
      flightUrl: "https://www.hawaii.gov/lih/",
      flightLabel: "LIH Airport Website",
      featured: true,
    },
    {
      code: "HNL",
      name: "Daniel K. Inouye International Airport",
      distance: "Inter-island flight — Honolulu",
      address: "300 Rodgers Blvd, Honolulu, HI 96819",
      note: "Major international hub. Many Kauai travelers connect through HNL for international destinations.",
      flightUrl: "https://www.hawaii.gov/hnl/",
      flightLabel: "HNL Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Lihue and Kauai.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Kauai travelers renew fast and avoid delays — no agency visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants typically require an in-person acceptance clerk visit. We guide Kauai residents through every step.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements often require additional forms. We guide you through exactly what's needed to avoid costly mistakes.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions for Kauai families.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports for Kauai residents.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: "Need a second passport for frequent travel, or replacing a damaged one? We handle both efficiently.",
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Lihue and Kauai travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for Kauai residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for Kauai residents with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for Kauai residents with lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16 on Kauai, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service for Kauai residents after marriage, divorce, or legal name changes." },
    { name: "Second Passport & Damaged", description: "Second passport issuance for frequent Kauai travelers and damaged passport replacement." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many Kauai travelers assume 6–8 weeks is safe, only to find out it's now 3–4 months. Apply as soon as you book travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you're turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you'd think." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option from Lihue or Kapaa." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you don't have to fly to Honolulu." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up." },
  ],
  processingOptions: [
    { speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: true },
    { speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: false, smartSaver: true },
    { speed: "1–2", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400", features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"], highlight: false, muted: true },
    { speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500", features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"], highlight: false, emergency: true },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — fly to Honolulu", fpc: "Most travelers ship documents overnight from Lihue", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documents", fpcGood: true },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Honolulu", route: "/cities/honolulu" },
    { name: "Kailua", route: "/cities/kailua" },
    { name: "Kahului", route: "/cities/kahului" },
    { name: "Hilo", route: "/cities/hilo" },
    { name: "Kailua-Kona", route: "/cities/kailua-kona" },
    { name: "Kapolei", route: "/cities/kapolei" },
  ],
  fpcOfficeAddress: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your Lihue or Kapaa home, ship with our prepaid label from any UPS/FedEx location near Lihue Airport, or schedule a drop-off. Most Kauai clients ship overnight.",
  fpcOfficeDistance: "Serving Lihue and all of Kauai remotely",
  serviceAreaIntro: "Whether you are in Lihue, Kapaa, Koloa, Poipu, or anywhere on the Garden Isle, Fast Passport Center makes expedited passport processing simple — no flying to Honolulu, no agency lines.",
  nearbyCitiesIntro: "Anywhere on Kauai — start online, ship overnight from Lihue or Kapaa, and we handle the rest.",
  comparisonIntro: "",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Lihue as simple as possible.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Kauai travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from real travelers across Lihue, Kapaa, and Poipu who trusted Fast Passport Center with their expedited passports.",
  faqIntro: "Everything Kauai travelers ask us most often about expedited passport services.",
  resourcesIntro: "",
  localTravelThemes: [],
  landmarks: [],
  island: "Kauai",
  islandNickname: "The Garden Isle",
  landmarkCards: [
    {
      name: "Na Pali Coast",
      description: "The most dramatic coastline in Hawaii — emerald sea cliffs plunging 4,000 feet into the Pacific. The Na Pali Coast is accessible by boat, helicopter, or the Kalalau Trail. International visitors come from everywhere to see it.",
      imageUrl: "https://readdy.ai/api/search-image?query=Na%20Pali%20Coast%20Kauai%20Hawaii%2C%20dramatic%20green%20sea%20cliffs%20with%20waterfalls%2C%20turquoise%20Pacific%20Ocean%20below%2C%20lush%20tropical%20vegetation%20on%20cliff%20faces%2C%20warm%20sunlight%2C%20professional%20aerial%20travel%20photography%2C%20stunning%20natural%20landmark%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=lihue-napali&orientation=landscape",
    },
    {
      name: "Waimea Canyon",
      description: "Called the 'Grand Canyon of the Pacific,' Waimea Canyon stretches 14 miles long and 3,600 feet deep. Its red and orange volcanic walls make it unlike anything else in Hawaii or the Pacific.",
      imageUrl: "https://readdy.ai/api/search-image?query=Waimea%20Canyon%20Kauai%20Hawaii%2C%20Grand%20Canyon%20of%20the%20Pacific%2C%20dramatic%20red%20and%20orange%20volcanic%20canyon%20walls%2C%20lush%20tropical%20vegetation%2C%20dramatic%20clouds%20and%20sunlight%2C%20professional%20landscape%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=lihue-waimea-canyon&orientation=landscape",
    },
    {
      name: "Kalapaki Bay",
      description: "Lihue's own beach, Kalapaki Bay fronts the Marriott resort and is the heart of Kauai's civic waterfront. It's where many of the island's international visitors first set eyes on the Garden Isle.",
      imageUrl: "https://readdy.ai/api/search-image?query=Kalapaki%20Bay%20Lihue%20Kauai%20Hawaii%2C%20calm%20golden%20sand%20bay%20with%20gentle%20waves%2C%20palm%20trees%2C%20Marriott%20resort%20in%20background%2C%20warm%20tropical%20sunlight%2C%20professional%20travel%20photography%2C%20inviting%20beach%20atmosphere%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=lihue-kalapaki&orientation=landscape",
    },
  ],
  travelTips: [
    { icon: "ri-flight-takeoff-line", title: "LIH Has Direct Mainland Flights", description: "Lihue Airport (LIH) has direct flights to Los Angeles, San Francisco, and Seattle. International connections go through Honolulu. Validate passport months before booking international itineraries." },
    { icon: "ri-map-pin-line", title: "No Passport Agency on Kauai", description: "The nearest regional passport agency is in Honolulu — an inter-island flight away. Fast Passport Center means you never have to leave the Garden Isle for passport processing." },
    { icon: "ri-hotel-line", title: "Resort Guest Emergencies", description: "Visitors at Princeville Resort, Grand Hyatt Poipu, or St. Regis Princeville who discover passport problems can ship documents urgently from Lihue FedEx. We return to your resort." },
    { icon: "ri-truck-line", title: "Ship from Lihue Airport Area", description: "FedEx near Lihue Airport and UPS at Kukui Grove Center handle overnight shipping for Kauai residents. Ship before 6pm for next-morning delivery to our processing center." },
    { icon: "ri-leaf-line", title: "Eco-Tourism & Adventure Travel", description: "Kauai's eco-tourism — hiking Kalalau, helicopter tours of Na Pali, kayaking Wailua River — inspires further international adventure travel. Keep passport validity current." },
    { icon: "ri-global-line", title: "Hollywood & International Visibility", description: "Kauai has been featured in over 60 major films. International film crews and high-profile visitors regularly need urgent passport processing while on-island." },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (Hawaii Vital Records)", url: "https://health.hawaii.gov/vitalrecords/", desc: "Hawaii Department of Health — Vital Records for birth and death certificates statewide" },
    { label: "Marriage Licenses (Kauai County)", url: "https://www.kauai.gov/", desc: "Kauai County for Lihue and Garden Isle" },
    { label: "Divorce Decrees (Kauai County)", url: "https://www.courts.state.hi.us/", desc: "Kauai County courts for divorce decree records" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Hawaii vital records online via VitalChek" },
  ],
  statePageRoute: "/states/hawaii",
  statePageName: "Hawaii",
  dmvWebsite: "https://www.hawaii.gov/dot/highways/mvm",
  dmvLabel: "Hawaii DMV — Official Website",
};